import React, { useEffect, useMemo, useState, useRef } from "react";
import { FiEdit } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Card, Col, Form, Modal, Row } from "react-bootstrap";
import { getApiCall, postApiCall } from "../../../service/httpCall";
import {
  API_APIWEBHOOK_GET_ONE,
  API_APIWEBHOOK_UPDATE,
  API_APIWEBHOOK_REQUEST_PARAMETERS_LIST,
} from "../../../service/url";
import { ROUTES } from "../../../utilities/constant";
import ApiBuilder from "./components/ApiBuilder";
import CopyButton from "./components/CopyButton";
import { syntaxHighlight } from "./components/apiBuilderUtils";
import { toast } from "react-toastify";

import "./create.css";

const EditApiWebhook = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const [webhook_name, setWebhookName] = useState("");
  const [request_type, setRequestType] = useState("");
  const [url, setUrl]         = useState("");
  const [body_text, setBodyText] = useState("");
  const [payload, setPayload]   = useState("");
  const [header, setHeader]     = useState("");

  // Postman container state
  const [bodyViewMode, setBodyViewMode]     = useState("pretty");
  const [bodyTab, setBodyTab]               = useState("body");
  const [responseHeaders, setResponseHeaders] = useState({});
  const [responseStatus, setResponseStatus]   = useState(null);
  const [responseParams, setResponseParams]   = useState([]);
  const [selectedParams, setSelectedParams]   = useState(new Set());
  const [parameterJson, setParameterJson]     = useState("");

  const [requestParamOptions, setRequestParamOptions] = useState([]);
  const [selectedRequestParams, setSelectedRequestParams] = useState([]);
  const [fixedJsonRaw, setFixedJsonRaw] = useState(null);
  const [paramDropdownOpen, setParamDropdownOpen] = useState(false);
  const paramDropdownRef = useRef(null);
  const [paramCustomValues, setParamCustomValues] = useState({});
  const [editingParamId, setEditingParamId] = useState(null);
  const [editingParamValue, setEditingParamValue] = useState("");
  const [requestBodySnapshot, setRequestBodySnapshot] = useState("");

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (paramDropdownRef.current && !paramDropdownRef.current.contains(e.target)) {
        setParamDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const toggleRequestParam = (id) => {
    if (selectedRequestParams.includes(id)) {
      setSelectedRequestParams((prev) => prev.filter((v) => v !== id));
      return;
    }
    const newOpt = requestParamOptions.find((o) => o.value === id);
    const newValue = paramCustomValues[id] ?? newOpt?.parameter_value;
    const conflictId = selectedRequestParams.find((existingId) => {
      const existingOpt = requestParamOptions.find((o) => o.value === existingId);
      return (paramCustomValues[existingId] ?? existingOpt?.parameter_value) === newValue;
    });
    if (conflictId) {
      const conflictOpt = requestParamOptions.find((o) => o.value === conflictId);
      toast.error(`"${newValue}" is already used by parameter "${conflictOpt?.parameter_name}"`);
      return;
    }
    setSelectedRequestParams((prev) => [...prev, id]);
  };

  const startEditParam = (id) => {
    const opt = requestParamOptions.find((o) => o.value === id);
    setEditingParamId(id);
    setEditingParamValue(paramCustomValues[id] ?? opt?.parameter_value ?? "");
  };

  const saveEditParam = () => {
    if (!editingParamValue.trim()) {
      toast.error("Parameter value cannot be empty");
      return;
    }
    const conflictId = selectedRequestParams.find((id) => {
      if (id === editingParamId) return false;
      const opt = requestParamOptions.find((o) => o.value === id);
      return (paramCustomValues[id] ?? opt?.parameter_value) === editingParamValue;
    });
    if (conflictId) {
      const conflictOpt = requestParamOptions.find((o) => o.value === conflictId);
      toast.error(`"${editingParamValue}" is already used by parameter "${conflictOpt?.parameter_name}"`);
      return;
    }
    setParamCustomValues((prev) => ({ ...prev, [editingParamId]: editingParamValue }));
    setEditingParamId(null);
    setEditingParamValue("");
  };

  const cancelEditParam = () => {
    setEditingParamId(null);
    setEditingParamValue("");
  };

  const apiMethod = useMemo(() => request_type || "GET", [request_type]);

  useEffect(() => {
    getApiCall(API_APIWEBHOOK_REQUEST_PARAMETERS_LIST)
      .then((data) => {
        const active = (data || []).filter((p) => p.status === 1 || p.status === "1");
        setRequestParamOptions(
          active.map((p) => ({
            label: p.parameter_name,
            value: p.id,
            parameter_name: p.parameter_name,
            parameter_value: p.parameter_value,
          }))
        );
      })
      .catch(() => {
        toast.error("Failed to load request parameters");
      });
  }, []);

  useEffect(() => {
    if (!fixedJsonRaw || requestParamOptions.length === 0) return;
    try {
      const parsed = JSON.parse(fixedJsonRaw);
      const preSelected = [];
      const customVals = {};
      requestParamOptions.forEach((opt) => {
        if (Object.prototype.hasOwnProperty.call(parsed, opt.parameter_name)) {
          preSelected.push(opt.value);
          const storedVal = String(parsed[opt.parameter_name]);
          if (storedVal !== opt.parameter_value) {
            customVals[opt.value] = storedVal;
          }
        }
      });
      setSelectedRequestParams(preSelected);
      if (Object.keys(customVals).length > 0) setParamCustomValues(customVals);
    } catch {}
  }, [fixedJsonRaw, requestParamOptions]);

  const fixedJson = useMemo(() => {
    if (selectedRequestParams.length === 0) return null;
    const result = {};
    selectedRequestParams.forEach((id) => {
      const found = requestParamOptions.find((opt) => opt.value === id);
      if (found) result[found.parameter_name] = paramCustomValues[id] ?? found.parameter_value;
    });
    return Object.keys(result).length > 0 ? JSON.stringify(result) : null;
  }, [selectedRequestParams, requestParamOptions, paramCustomValues]);

  useEffect(() => {}, [fixedJson, request_type]);

  const initialHeaders = useMemo(() => {
    const h = (header || "").trim();
    if (!h) return [{ key: "", value: "" }];
    try {
      const parsed = JSON.parse(h);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return [{ key: "", value: "" }];
      const arr = Object.entries(parsed).map(([k, v]) => ({
        key: String(k),
        value: typeof v === "string" ? v : JSON.stringify(v),
      }));
      return arr.length ? arr : [{ key: "", value: "" }];
    } catch {
      return [{ key: "", value: "" }];
    }
  }, [header]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const resp = await getApiCall(API_APIWEBHOOK_GET_ONE.replace(":id", id), {});
        const reqType = resp?.request_type === "WHATSAPP" ? "POST" : (resp?.request_type || "");
        const bodyStr = resp?.api_text || resp?.body_text || "";
        const payloadStr = resp?.payload || "";

        setWebhookName(resp?.webhook_name || "");
        setRequestType(reqType);
        setUrl(resp?.url || "");
        setBodyText(bodyStr);
        setPayload(payloadStr);
        setHeader(resp?.header || "");
        setParameterJson(resp?.response_parameters || resp?.parameter_json || "");
        setFixedJsonRaw(resp?.fixed_json || null);
        if (["POST"].includes(reqType)) {
          setRequestBodySnapshot(payloadStr || "");
        }

        const bodyForParams = reqType === "GET" ? bodyStr : payloadStr;
        if (bodyForParams) {
          try {
            const parsed = JSON.parse(bodyForParams);
            const source = Array.isArray(parsed) ? parsed[0] : parsed;
            if (source && typeof source === "object" && !Array.isArray(source)) {
              setResponseParams(
                Object.entries(source).map(([key, value]) => ({
                  key,
                  sampleValue:
                    value === null ? "null"
                    : typeof value === "object" ? JSON.stringify(value)
                    : String(value),
                }))
              );
            }
          } catch {}
        }
      } catch (err) {
        toast.error(typeof err === "string" ? err : "Failed to load webhook");
      } finally {
        setLoading(false);
      }
    };
    if (id) load();
  }, [id]);

  // ── Helpers ──────────────────────────────────────────────────
  const extractParams = (bodyString) => {
    const toSample = (v) =>
      v === null ? "null" : typeof v === "object" ? JSON.stringify(v) : String(v);
    try {
      const parsed = JSON.parse(bodyString);
      if (Array.isArray(parsed)) {
        const allParams = [];
        parsed.forEach((item, index) => {
          if (item && typeof item === "object" && !Array.isArray(item)) {
            Object.entries(item).forEach(([k, value]) => {
              allParams.push({ key: `[${index}].${k}`, displayKey: k, groupKey: `[${index}]`, sampleValue: toSample(value) });
            });
          }
        });
        return allParams;
      }
      if (parsed && typeof parsed === "object") {
        const allParams = [];
        Object.entries(parsed).forEach(([parentKey, parentValue]) => {
          if (Array.isArray(parentValue)) {
            parentValue.forEach((item, index) => {
              if (item && typeof item === "object" && !Array.isArray(item)) {
                Object.entries(item).forEach(([k, value]) => {
                  allParams.push({ key: `${parentKey}.[${index}].${k}`, displayKey: k, groupKey: `${parentKey}.[${index}]`, sampleValue: toSample(value) });
                });
              }
            });
          } else {
            allParams.push({ key: parentKey, displayKey: parentKey, sampleValue: toSample(parentValue) });
          }
        });
        return allParams;
      }
    } catch {}
    return [];
  };

  const handleUseUrl = (u) => setUrl(u || "");

  const handleViewModeChange = (mode) => setBodyViewMode(mode);

  const getLineCount = (val) => {
    const lines = (val || "").split("\n").length;
    return Array.from({ length: Math.max(lines, 1) }, (_, i) => i + 1);
  };

  const getStatusColor = (status) => {
    if (!status) return "#858585";
    if (status >= 200 && status < 300) return "#4caf50";
    if (status >= 300 && status < 400) return "#ff9800";
    if (status >= 400 && status < 500) return "#f44336";
    if (status >= 500) return "#e53935";
    return "#858585";
  };

  const handleBuilderResponse = (bodyString, headers, statusInfo) => {
    if (statusInfo) {
      // TEST button: store response in body_text for display (GET and POST).
      // Do NOT overwrite payload here — payload is the request body the user typed.
      setBodyText(bodyString || "");
      if (headers && Object.keys(headers).length) setResponseHeaders(headers);
      setResponseStatus(statusInfo);
      const params = extractParams(bodyString || "");
      setResponseParams(params);
      setSelectedParams(new Set());
      setParameterJson("");
      setBodyTab("body");
    } else {
      // ApiBuilder Submit button: capture the request body into payload.
      if (request_type === "GET") {
        setBodyText(bodyString || "");
      } else {
        setPayload(bodyString || "");
      }
    }
  };

  const handleParamToggle = (key) => {
    setSelectedParams((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const handleSelectAll   = () => setSelectedParams(new Set(responseParams.map((p) => p.key)));
  const handleDeselectAll = () => { setSelectedParams(new Set()); setParameterJson(""); };

  const handleSaveParams = () => {
    const result = {};
    responseParams
      .filter((p) => selectedParams.has(p.key))
      .forEach((p) => { result[p.key] = `{${p.key}}`; });
    setParameterJson(JSON.stringify(result, null, 2));
  };

  const handleBuilderClear = () => {
    setBodyText("");
    setPayload("");
    setResponseHeaders({});
    setResponseStatus(null);
    setBodyTab("body");
    setBodyViewMode("pretty");
    setResponseParams([]);
    setSelectedParams(new Set());
    setParameterJson("");
  };

  const handleApiBuilderChange = (apiState) => {
    setUrl(apiState?.url || "");
    if (!apiState) { setHeader(""); return; }

    // Sync headers
    if (apiState.headerMode === "raw") {
      setHeader((apiState.rawHeaders || "").trim());
    } else {
      const headerObj = (apiState.headers || []).reduce((acc, h) => {
        const key = (h?.key || "").trim();
        if (!key) return acc;
        acc[key] = h?.value ?? "";
        return acc;
      }, {});
      setHeader(Object.keys(headerObj).length ? JSON.stringify(headerObj) : "");
    }

    // Keep payload in sync with ApiBuilder request body for POST.
    if (["POST"].includes(request_type)) {
      if (apiState.bodyMode === "raw") {
        setPayload(apiState.rawBody || "");
      } else {
        const bodyObj = (apiState.body || []).reduce((acc, { key, value }) => {
          if (!(key || "").trim()) return acc;
          acc[key.trim()] = value;
          return acc;
        }, {});
        setPayload(Object.keys(bodyObj).length ? JSON.stringify(bodyObj, null, 2) : "");
      }
    }
  };

  // ── Validation ───────────────────────────────────────────────
  const validate = () => {
    const name = webhook_name.trim();
    const u    = url.trim();
    const type = request_type;
    const urlRegex = /^(https?:\/\/)([\w\-._~:/?#[\]@!$&'()*+,;=]+)$/i;

    if (!name || !u || !type) { toast.error("Please fill all required fields."); return false; }
    if (!urlRegex.test(u))    { toast.error("Please enter a valid Webhook URL (must start with http:// or https://)."); return false; }
    if (header && header.trim()) {
      try { JSON.parse(header); } catch { toast.error("Headers must be valid JSON."); return false; }
    }
    if (["POST"].includes(type) && !payload.trim()) { toast.error("Payload is required for this request type."); return false; }
    return true;
  };

  const handleSubmit = (e) => { e.preventDefault(); if (!validate()) return; setShowConfirmModal(true); };

  const submitWebhook = async () => {
    setShowConfirmModal(false);
    setSaving(true);
    const body = {
      webhook_name:   webhook_name.trim(),
      url:            url.trim(),
      request_type:   request_type,
      body_text:      null,
      api_text:       request_type === "GET" ? body_text.trim() : null,
      payload:        ["POST"].includes(request_type) ? (payload && payload.trim() ? payload.trim() : null) : null,
      header:         header && header.trim() ? header.trim() : null,
      response_parameters: parameterJson && parameterJson.trim() ? parameterJson.trim() : null,
      fixed_json: fixedJson,
      status:         "1",
    };
    try {
      await postApiCall(API_APIWEBHOOK_UPDATE.replace(":id", id), body);
      toast.success("Webhook updated successfully!");
      navigate(`/${ROUTES.APIWEBHOOK.LIST}`);
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-4">Loading...</div>;

  return (
    <>
      <div className="page-wrapper">
        <Card className="page-card">
          <Card.Header className="page-header">Edit API Webhook</Card.Header>
          <Card.Body>
            <Row className="g-4">
              <Col xs={12} md={12} className="left-panel">
                <Card className="section-card">
                  <Card.Body>
                    <Form onSubmit={handleSubmit}>
                      <Row>
                        <Col sm={6}>
                          <Form.Group className="form-group required">
                            <Form.Label>
                              Webhook Name:<span style={{ color: "red", margin: "0 4px" }}>*</span>
                            </Form.Label>
                            <Form.Control
                              required
                              type="text"
                              placeholder="Webhook Name"
                              value={webhook_name}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (/^[A-Za-z]*$/.test(value)) setWebhookName(value);
                              }}
                              autoComplete="off"
                              pattern="^[A-Za-z]+$"
                              title="Only letters allowed. No numbers, spaces, or special characters."
                            />
                          </Form.Group>
                        </Col>
                        <Col sm={6}>
                          <Form.Group className="form-group required">
                            <Form.Label>
                              Request Type:<span style={{ color: "red", margin: "0 4px" }}>*</span>
                            </Form.Label>
                            <Form.Select
                              required
                              value={request_type}
                              onChange={(e) => setRequestType(e.target.value)}
                            >
                              <option value="">Select Request Type</option>
                              <option value="GET">GET</option>
                              <option value="POST">POST</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      </Row>

                      {request_type && (
                        <Row className="mb-2">
                          <Col>
                            <ApiBuilder
                              initialUrl={url}
                              initialMethod={apiMethod}
                              initialHeaders={initialHeaders}
                              initialRawBody={["POST"].includes(request_type) ? requestBodySnapshot : ""}
                              onUseUrl={handleUseUrl}
                              onMethod={(m) => {
                                const next = String(m || "").toUpperCase() === "GET" ? "GET" : "POST";
                                setRequestType(next);
                              }}
                              onResponse={handleBuilderResponse}
                              onStateChange={handleApiBuilderChange}
                              onClear={handleBuilderClear}
                            />
                          </Col>
                        </Row>
                      )}

                      {request_type && (
                        <Row className="mt-3 align-items-start">
                          <Col sm={selectedRequestParams.length > 0 ? 6 : 12}>
                            <Form.Group className="form-group">
                              <Form.Label>Request Parameters</Form.Label>
                              <div ref={paramDropdownRef} style={{ position: "relative" }}>
                                {/* Trigger */}
                                <div
                                  onClick={() => requestParamOptions.length > 0 && setParamDropdownOpen((v) => !v)}
                                  style={{
                                    border: "1px solid #ced4da",
                                    borderRadius: "4px",
                                    minHeight: "42px",
                                    padding: "6px 36px 6px 10px",
                                    cursor: requestParamOptions.length === 0 ? "not-allowed" : "pointer",
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: "6px",
                                    alignItems: "center",
                                    backgroundColor: requestParamOptions.length === 0 ? "#e9ecef" : "#fff",
                                    position: "relative",
                                    userSelect: "none",
                                  }}
                                >
                                  {selectedRequestParams.length === 0 ? (
                                    <span style={{ color: "#6c757d", fontSize: "14px" }}>
                                      {requestParamOptions.length === 0 ? "No parameters available" : "Select Request Parameters"}
                                    </span>
                                  ) : (
                                    selectedRequestParams.map((id) => {
                                      const opt = requestParamOptions.find((o) => o.value === id);
                                      return opt ? (
                                        <span
                                          key={id}
                                          style={{
                                            background: "#e2e8f0",
                                            border: "1px solid #cbd5e1",
                                            borderRadius: "4px",
                                            padding: "2px 8px",
                                            fontSize: "13px",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: "5px",
                                            color: "#374151",
                                          }}
                                        >
                                          {opt.label}
                                          <span
                                            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); toggleRequestParam(id); }}
                                            style={{ cursor: "pointer", color: "#6b7280", fontWeight: "bold", lineHeight: 1 }}
                                          >×</span>
                                        </span>
                                      ) : null;
                                    })
                                  )}
                                  <span style={{
                                    position: "absolute",
                                    right: "10px",
                                    top: "50%",
                                    transform: `translateY(-50%) rotate(${paramDropdownOpen ? 180 : 0}deg)`,
                                    transition: "transform 0.2s",
                                    color: "#6c757d",
                                    fontSize: "11px",
                                    pointerEvents: "none",
                                  }}>▼</span>
                                </div>

                                {/* Dropdown Panel */}
                                {paramDropdownOpen && (
                                  <div style={{
                                    position: "absolute",
                                    top: "calc(100% + 4px)",
                                    left: 0,
                                    right: 0,
                                    zIndex: 1050,
                                    border: "1px solid #ced4da",
                                    borderRadius: "4px",
                                    backgroundColor: "#fff",
                                    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                                    maxHeight: "220px",
                                    overflowY: "auto",
                                  }}>
                                    {requestParamOptions.map((opt) => {
                                      const checked = selectedRequestParams.includes(opt.value);
                                      return (
                                        <div
                                          key={opt.value}
                                          onMouseDown={(e) => { e.preventDefault(); toggleRequestParam(opt.value); }}
                                          style={{
                                            padding: "9px 14px",
                                            cursor: "pointer",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "10px",
                                            backgroundColor: checked ? "#f0f4ff" : "#fff",
                                            borderBottom: "1px solid #f1f3f5",
                                          }}
                                        >
                                          <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => {}}
                                            style={{ width: "15px", height: "15px", cursor: "pointer", accentColor: "#0d6efd" }}
                                          />
                                          <span style={{ fontSize: "14px", color: "#374151" }}>{opt.label}</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </Form.Group>
                            </Col>
                            {selectedRequestParams.length > 0 && (
                              <Col sm={6}>
                              <div style={{ border: "1px solid #e2e8f0", borderRadius: "6px", overflow: "hidden" }}>
                                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                                  <thead>
                                    <tr style={{ backgroundColor: "#f8fafc" }}>
                                      <th style={{ padding: "8px 12px", textAlign: "left", borderBottom: "1px solid #e2e8f0", color: "#475569", fontWeight: 600 }}>Parameter Name</th>
                                      <th style={{ padding: "8px 12px", textAlign: "left", borderBottom: "1px solid #e2e8f0", color: "#475569", fontWeight: 600 }}>Value</th>
                                      <th style={{ padding: "8px 12px", textAlign: "center", borderBottom: "1px solid #e2e8f0", color: "#475569", fontWeight: 600, width: "70px" }}>Action</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {selectedRequestParams.map((id) => {
                                      const opt = requestParamOptions.find((o) => o.value === id);
                                      if (!opt) return null;
                                      const currentValue = paramCustomValues[id] ?? opt.parameter_value;
                                      const isEditing = editingParamId === id;
                                      return (
                                        <tr key={id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                                          <td style={{ padding: "8px 12px", color: "#374151", fontWeight: 500 }}>{opt.parameter_name}</td>
                                          <td style={{ padding: "8px 12px" }}>
                                            {isEditing ? (
                                              <input
                                                type="text"
                                                value={editingParamValue}
                                                onChange={(e) => setEditingParamValue(e.target.value)}
                                                onKeyDown={(e) => { if (e.key === "Enter") saveEditParam(); if (e.key === "Escape") cancelEditParam(); }}
                                                autoFocus
                                                style={{ border: "1px solid #3b82f6", borderRadius: "4px", padding: "4px 8px", fontSize: "13px", width: "100%", outline: "none", boxSizing: "border-box" }}
                                              />
                                            ) : (
                                              <span style={{ color: paramCustomValues[id] !== undefined ? "#0d6efd" : "#6b7280" }}>{currentValue}</span>
                                            )}
                                          </td>
                                          <td style={{ padding: "8px 12px", textAlign: "center" }}>
                                            {isEditing ? (
                                              <span style={{ display: "inline-flex", gap: "8px", alignItems: "center" }}>
                                                <button type="button" onClick={saveEditParam} title="Save" style={{ background: "none", border: "none", cursor: "pointer", color: "#16a34a", fontSize: "16px", padding: 0, lineHeight: 1 }}>✓</button>
                                                <button type="button" onClick={cancelEditParam} title="Cancel" style={{ background: "none", border: "none", cursor: "pointer", color: "#dc2626", fontSize: "16px", padding: 0, lineHeight: 1 }}>✕</button>
                                              </span>
                                            ) : (
                                              <button type="button" onClick={() => startEditParam(id)} title="Edit value" style={{ background: "none", border: "none", cursor: "pointer", color: "#3b82f6", padding: "2px", display: "inline-flex", alignItems: "center" }}>
                                                <FiEdit size={14} />
                                              </button>
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                              </Col>
                            )}
                          {fixedJson && (
                            <Col sm={12} className="mt-3">
                              <Form.Group className="form-group">
                                <Form.Label>Fixed JSON Preview</Form.Label>
                                <pre
                                  style={{
                                    border: "1px solid #dee2e6",
                                    borderRadius: "4px",
                                    padding: "8px 12px",
                                    minHeight: "42px",
                                    margin: 0,
                                    backgroundColor: "#f8f9fa",
                                    fontFamily: "monospace",
                                    fontSize: "13px",
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-all",
                                    color: "#212529",
                                  }}
                                >
                                  {JSON.stringify(JSON.parse(fixedJson), null, 2)}
                                </pre>
                              </Form.Group>
                            </Col>
                          )}
                        </Row>
                      )}

                      {(request_type === "GET" || ["POST"].includes(request_type)) && (
                        <Row className="mt-3 align-items-start">
                          <Col md={responseParams.length > 0 ? 7 : 12} className="mb-3 mb-md-0">
                            <div className="postman-body-container">
                              {/* Main tab bar */}
                              <div className="postman-main-tabbar">
                                <button
                                  type="button"
                                  className={`postman-main-tab ${bodyTab === "body" ? "active" : ""}`}
                                  onClick={() => setBodyTab("body")}
                                >
                                  {request_type === "GET" ? "Body Text" : "Payload"}
                                  {request_type === "GET" && <span className="postman-required">*</span>}
                                </button>
                                <button
                                  type="button"
                                  className={`postman-main-tab ${bodyTab === "headers" ? "active" : ""}`}
                                  onClick={() => setBodyTab("headers")}
                                >
                                  Headers
                                  {Object.keys(responseHeaders).length > 0 && (
                                    <span className="postman-count-badge">
                                      {Object.keys(responseHeaders).length}
                                    </span>
                                  )}
                                </button>

                                {bodyTab === "body" && (
                                  <div className="postman-body-inline-controls">
                                    <div className="postman-tabs">
                                      <button
                                        type="button"
                                        className={`postman-tab ${bodyViewMode === "raw" ? "active" : ""}`}
                                        onClick={() => handleViewModeChange("raw")}
                                      >
                                        Raw
                                      </button>
                                      <button
                                        type="button"
                                        className={`postman-tab ${bodyViewMode === "pretty" ? "active" : ""}`}
                                        onClick={() => handleViewModeChange("pretty")}
                                      >
                                        Pretty
                                      </button>
                                    </div>
                                    <span className="postman-badge">JSON</span>
                                    <CopyButton text={request_type === "GET" ? body_text : payload} />
                                  </div>
                                )}
                              </div>

                              {/* Body panel */}
                              {bodyTab === "body" && (
                                <div className="postman-editor-wrap">
                                  {bodyViewMode === "raw" && (
                                    <div className="postman-line-numbers">
                                      {getLineCount(request_type === "GET" ? body_text : payload).map((n) => (
                                        <span key={n}>{n}</span>
                                      ))}
                                    </div>
                                  )}
                                  {bodyViewMode === "pretty" ? (
                                    <pre
                                      className="postman-pretty-view"
                                      dangerouslySetInnerHTML={{
                                        __html: (() => {
                                          // Pretty view shows the API response (body_text) after Test;
                                          // before Test it shows the request body (payload) for POST.
                                          const raw = request_type === "GET" || responseStatus ? body_text : payload;
                                          try {
                                            return syntaxHighlight(JSON.parse(raw));
                                          } catch {
                                            return raw
                                              .replace(/&/g, "&amp;")
                                              .replace(/</g, "&lt;")
                                              .replace(/>/g, "&gt;");
                                          }
                                        })(),
                                      }}
                                    />
                                  ) : request_type === "GET" ? (
                                    <Form.Control
                                      name="body_text"
                                      value={body_text}
                                      as="textarea"
                                      className="postman-textarea"
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        if (/^\s+$/.test(value)) return;
                                        setBodyText(value);
                                      }}
                                      onBlur={(e) => setBodyText(e.target.value.trim())}
                                      spellCheck={false}
                                    />
                                  ) : (
                                    <Form.Control
                                      required
                                      name="payload"
                                      value={payload}
                                      as="textarea"
                                      className="postman-textarea"
                                      onChange={(e) => setPayload(e.target.value)}
                                      spellCheck={false}
                                    />
                                  )}
                                </div>
                              )}

                              {/* Headers panel */}
                              {bodyTab === "headers" && (
                                <div className="postman-headers-panel">
                                  {Object.keys(responseHeaders).length === 0 ? (
                                    <p className="postman-headers-empty">
                                      No response headers yet. Click <strong>Test</strong> to send a request.
                                    </p>
                                  ) : (
                                    <table className="postman-headers-table">
                                      <thead>
                                        <tr>
                                          <th>Header</th>
                                          <th>Value</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {Object.entries(responseHeaders).map(([k, v]) => (
                                          <tr key={k}>
                                            <td className="postman-header-key">{k}</td>
                                            <td className="postman-header-val">{v}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  )}
                                </div>
                              )}

                              {/* Status meta */}
                              {responseStatus && (
                                <div className="postman-response-meta">
                                  <span>Status:</span>
                                  <span
                                    className="postman-status-pill"
                                    style={{
                                      background: getStatusColor(responseStatus.status) + "22",
                                      color: getStatusColor(responseStatus.status),
                                      borderColor: getStatusColor(responseStatus.status) + "55",
                                    }}
                                  >
                                    {responseStatus.status} {responseStatus.statusText}
                                  </span>
                                </div>
                              )}
                            </div>
                          </Col>

                          {responseParams.length > 0 && (
                            <Col md={5}>
                              <div className="param-picker-container">
                                <div className="param-picker-header">
                                  <span className="param-picker-title">Response Parameters</span>
                                  <span className="param-picker-hint">Select parameters to save</span>
                                  <div className="param-picker-actions">
                                    <button type="button" className="param-link-btn select-all-btn" onClick={handleSelectAll}>
                                      Select All
                                    </button>
                                    <button type="button" className="param-link-btn deselect-all-btn" onClick={handleDeselectAll}>
                                      Deselect All
                                    </button>
                                  </div>
                                </div>

                                <div className="param-list-head">
                                  <span style={{ width: 16, flexShrink: 0 }} />
                                  <span className="param-col-label key-col">Parameter</span>
                                  <span className="param-col-label val-col">Sample Value</span>
                                </div>

                                <div className="param-picker-body">
                                  <div className="param-list">
                                    {(() => {
                                      const segments = [];
                                      let curGroup = null;
                                      responseParams.forEach((p) => {
                                        if (p.groupKey) {
                                          if (!curGroup || curGroup.groupKey !== p.groupKey) {
                                            curGroup = { type: "group", groupKey: p.groupKey, items: [p] };
                                            segments.push(curGroup);
                                          } else {
                                            curGroup.items.push(p);
                                          }
                                        } else {
                                          curGroup = null;
                                          segments.push({ type: "flat", item: p });
                                        }
                                      });
                                      return segments.map((seg) =>
                                        seg.type === "group" ? (
                                          <div key={seg.groupKey} className="param-index-group">
                                            <div className="param-index-header">{seg.groupKey}</div>
                                            {seg.items.map(({ key, displayKey, sampleValue }) => (
                                              <label key={key} className={`param-item ${selectedParams.has(key) ? "selected" : ""}`}>
                                                <input type="checkbox" className="param-checkbox" checked={selectedParams.has(key)} onChange={() => handleParamToggle(key)} />
                                                <span className="param-key">{displayKey}</span>
                                                <span className="param-sample">{sampleValue}</span>
                                              </label>
                                            ))}
                                          </div>
                                        ) : (
                                          <label key={seg.item.key} className={`param-item ${selectedParams.has(seg.item.key) ? "selected" : ""}`}>
                                            <input type="checkbox" className="param-checkbox" checked={selectedParams.has(seg.item.key)} onChange={() => handleParamToggle(seg.item.key)} />
                                            <span className="param-key">{seg.item.displayKey || seg.item.key}</span>
                                            <span className="param-sample">{seg.item.sampleValue}</span>
                                          </label>
                                        )
                                      );
                                    })()}
                                  </div>
                                </div>

                                <div className="param-picker-footer">
                                  <span className="param-selected-count">
                                    <span className="param-count-pill">{selectedParams.size}</span>
                                    of {responseParams.length} selected
                                  </span>
                                  <button
                                    type="button"
                                    className="param-save-btn"
                                    disabled={selectedParams.size === 0}
                                    onClick={handleSaveParams}
                                  >
                                    Save Parameters
                                  </button>
                                </div>
                              </div>
                            </Col>
                          )}
                        </Row>
                      )}

                      {parameterJson && (
                        <Row className="mt-3">
                          <Col>
                            <div className="param-result">
                              <div className="param-result-header">
                                <span>parameterJson</span>
                                <CopyButton text={parameterJson} />
                              </div>
                              <pre className="param-result-pre">{parameterJson}</pre>
                            </div>
                          </Col>
                        </Row>
                      )}

                      <div className="d-flex justify-content-end gap-2 mt-3">
                        <Button
                          variant="secondary"
                          onClick={() => navigate(`/${ROUTES.APIWEBHOOK.LIST}`)}
                        >
                          Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={saving}>
                          Save Changes
                        </Button>
                      </div>
                    </Form>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </div>

      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Submission</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure the {["POST"].includes(request_type) ? "payload" : "body text"} is correct?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>No</Button>
          <Button variant="primary" onClick={submitWebhook}>Yes</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default EditApiWebhook;
